import { useEffect, useState } from "react";
import { ChevronDown, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Spinner } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { StatusPill } from "@/components/privacy/RightsRequests";
import { Card, History, Level, Pick } from "@/pages/admin/privacy/shared";

const LEVELS = ["low", "medium", "high"];
const ACTIONS = [["draft", "Save draft"], ["in_review", "Submit for review"], ["changes_requested", "Request changes"], ["approved", "Approve"], ["rejected", "Reject"]];

const RiskRow = ({ r, i, set, remove, id }) => (
  <div className="grid gap-2 rounded-xl bg-[#F5F1EB] p-2 sm:grid-cols-[1fr_7rem_7rem_1fr_auto]" data-testid={`dpia-risk-${id}-${i}`}>
    <Input className="bg-white" value={r.risk} placeholder="Risk to people" onChange={(e) => set({ ...r, risk: e.target.value })} />
    <Pick value={r.likelihood} onChange={(v) => set({ ...r, likelihood: v })} options={LEVELS} className="w-full" />
    <Pick value={r.severity} onChange={(v) => set({ ...r, severity: v })} options={LEVELS} className="w-full" />
    <Input className="bg-white" value={r.mitigation} placeholder="Mitigation" onChange={(e) => set({ ...r, mitigation: e.target.value })} />
    <Button size="icon" variant="ghost" onClick={remove} aria-label="Remove risk"><X size={15} /></Button>
  </div>
);

const DpiaBody = ({ d, onDone }) => {
  const [f, setF] = useState({ description: d.description || "", necessity: d.necessity || "", data_categories: (d.data_categories || []).join(", "), residual_risk: d.residual_risk || "medium", review_due: d.review_due || "", risks: d.risks || [] });
  const [note, setNote] = useState("");
  const save = async (status) => {
    try {
      await api.patch(`/admin/privacy/dpias/${d.id}`, { ...f, data_categories: f.data_categories.split(",").map((s) => s.trim()).filter(Boolean), review_due: f.review_due || null, status, note });
      toast.success(status === d.status ? "Saved" : `DPIA ${status.replace("_", " ")}`); setNote(""); onDone();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const setRisk = (i, r) => setF({ ...f, risks: f.risks.map((x, j) => (j === i ? r : x)) });
  return (
    <div className="space-y-3 border-t border-border pt-3">
      <Textarea rows={2} value={f.description} placeholder="What the processing does" onChange={(e) => setF({ ...f, description: e.target.value })} data-testid={`dpia-description-${d.id}`} />
      <Textarea rows={2} value={f.necessity} placeholder="Why it's necessary and proportionate" onChange={(e) => setF({ ...f, necessity: e.target.value })} data-testid={`dpia-necessity-${d.id}`} />
      <Input value={f.data_categories} placeholder="Data categories, comma separated" onChange={(e) => setF({ ...f, data_categories: e.target.value })} data-testid={`dpia-categories-${d.id}`} />
      <div className="space-y-2"><p className="text-xs font-semibold text-slate-500">Risks — likelihood · severity</p>
        {f.risks.map((r, i) => <RiskRow key={i} r={r} i={i} id={d.id} set={(v) => setRisk(i, v)} remove={() => setF({ ...f, risks: f.risks.filter((_, j) => j !== i) })} />)}
        <Button size="sm" variant="outline" className="rounded-full" onClick={() => setF({ ...f, risks: [...f.risks, { risk: "", likelihood: "medium", severity: "medium", mitigation: "" }] })} data-testid={`dpia-add-risk-${d.id}`}><Plus size={14} className="mr-1" />Add risk</Button></div>
      <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">Residual risk <Pick value={f.residual_risk} onChange={(v) => setF({ ...f, residual_risk: v })} options={LEVELS} className="w-28" testid={`dpia-residual-${d.id}`} />
        Review by <Input type="date" className="w-40" value={f.review_due} onChange={(e) => setF({ ...f, review_due: e.target.value })} data-testid={`dpia-review-${d.id}`} /></div>
      <Input value={note} placeholder="Note for the history (optional)" onChange={(e) => setNote(e.target.value)} data-testid={`dpia-note-${d.id}`} />
      <div className="flex flex-wrap gap-2">{ACTIONS.map(([s, l]) => <Button key={s} size="sm" variant={s === "approved" ? "default" : "outline"} className="rounded-full" onClick={() => save(s)} data-testid={`dpia-action-${s}-${d.id}`}>{l}</Button>)}</div>
      <History items={d.history || []} />
    </div>
  );
};

const DpiaCard = ({ d, onDone }) => {
  const [open, setOpen] = useState(false);
  return (
    <Card testid={`dpia-${d.id}`}>
      <button className="flex w-full flex-wrap items-center gap-2 text-left" onClick={() => setOpen(!open)} data-testid={`dpia-toggle-${d.id}`}>
        <b className="font-serif text-lg">{d.title}</b><span className="text-xs text-slate-500">{d.feature}</span><StatusPill status={d.status} testid={`dpia-status-${d.id}`} />
        <span className="ml-auto flex items-center gap-2 text-xs text-slate-500">Residual <Level level={d.residual_risk || "medium"} />{d.risks?.length || 0} risks<ChevronDown size={16} className={`transition-transform ${open ? "rotate-180" : ""}`} /></span>
      </button>
      {open && <DpiaBody key={d.history?.length} d={d} onDone={onDone} />}
    </Card>
  );
};

export const Dpias = () => {
  const [rows, setRows] = useState(null);
  const [title, setTitle] = useState("");
  const [feature, setFeature] = useState("");
  const load = () => api.get("/admin/privacy/dpias").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const create = async () => { try { await api.post("/admin/privacy/dpias", { title, feature }); setTitle(""); setFeature(""); toast.success("DPIA started"); load(); } catch (e) { toast.error(errMsg(e)); } };
  if (!rows) return <Spinner />;
  return (
    <div className="space-y-3" data-testid="admin-dpias">
      <p className="text-sm text-slate-600">Data protection impact assessments for higher-risk features. Approve only after risks have mitigations.</p>
      <div className="flex flex-wrap gap-2"><Input className="min-w-0 flex-1" placeholder="New assessment title" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="dpia-new-title" />
        <Input className="w-48" placeholder="Feature" value={feature} onChange={(e) => setFeature(e.target.value)} data-testid="dpia-new-feature" />
        <Button className="rounded-full" disabled={!title.trim()} onClick={create} data-testid="dpia-create-btn"><Plus size={15} className="mr-1" />Start DPIA</Button></div>
      {rows.map((d) => <DpiaCard key={d.id} d={d} onDone={load} />)}
    </div>
  );
};
